import {
    OrderStepFields,
} from 'app/Apps/OrderManagement/Orders/orders.types'
import {
    LocationType, StepTypeDetailed,
    DefaultTruckingComment,
    TransportModeRoad, DeliveryOptionRoad,
} from 'app/enums/label.enums'
import {
    FieldInCard,
    NestedField,
} from 'app/types/form.types'

type Args = {
    pickupLocationId: number,
    deliveryLocationId: number,
    filterLocationType: LocationType,
    transportModeRoad: TransportModeRoad,
    exists: boolean,
    isFirstStep: boolean,
    stepTypeDetailed: StepTypeDetailed,
    deliveryOptionRoad: DeliveryOptionRoad,
    getAllowedValues: (enumType: Record<string, string>) => Record<string, string>
}

const getInternalAirFormFields = ({
    pickupLocationId,
    deliveryLocationId,
    filterLocationType,
    transportModeRoad,
    getAllowedValues,
    exists,
    isFirstStep,
    stepTypeDetailed,
    deliveryOptionRoad,
}: Args): FieldInCard<OrderStepFields>[] => {
    const trucksAndVans: FieldInCard<OrderStepFields>[] = [
        {
            name: 'deliveryOptionRoad',
            componentName: 'EnumSelector',
            allowedValues: getAllowedValues(DeliveryOptionRoad),
            required: true,
            className: 'deliveryOptionRoad',
            labelKey: 'OrderStep.deliveryOptionRoad',
        },
        ...deliveryOptionRoad === DeliveryOptionRoad.TRUCKS ? [{
            name: 'numberOfTrucks',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfTrucks',
        }] as FieldInCard<OrderStepFields>[] : [],
        ...deliveryOptionRoad === DeliveryOptionRoad.VANS ? [{
            name: 'numberOfVans',
            componentName: 'IntegerInput',
            className: 'numberOfVehicles',
            required: true,
            labelKey: 'OrderStep.numberOfVans',
        }] as FieldInCard<OrderStepFields>[] : [],
    ]

    const pickupLocationContactsByLocationField: NestedField<OrderStepFields>[] = [{
        name: 'pickupLocationContacts',
        componentName: 'MultipleContactByLocationObjectSelector',
        existedForm: exists,
        selectedLocation: pickupLocationId,
        labelKey: 'OrderStep.pickupLocationContacts',
    }]

    const pickupLocationContactsField: NestedField<OrderStepFields>[] = [{
        name: 'pickupLocationContacts',
        componentName: 'MultipleContactObjectSelector',
        labelKey: 'OrderStep.pickupLocationContacts',
    }]

    return [
        {
            id: 'column1',
            fields: [
                {
                    name: 'forwarder',
                    componentName: 'FreightForwarderSelector',
                    labelKey: 'OrderStep.forwarder',
                },
                {
                    name: 'forwarderContacts',
                    componentName: 'MultipleContactObjectSelector',
                    labelKey: 'OrderStep.forwarderContacts',
                },
                {
                    name: 'mawbReceived',
                    componentName: 'Checkbox',
                    labelKey: 'OrderStep.mawbReceived',
                },
                {
                    name: 'mawbNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'OrderStep.mawbNumber',
                },
                {
                    name: 'mawbDocumentCopy',
                    componentName: 'AttachmentsObjectSelector',
                    labelKey: 'OrderStep.mawbDocumentCopy',
                },
                {
                    name: 'hawbReceived',
                    componentName: 'Checkbox',
                    labelKey: 'OrderStep.hawbReceived',
                },
                {
                    name: 'hawbNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'OrderStep.hawbNumber',
                },
                {
                    name: 'hawbDocumentCopy',
                    componentName: 'AttachmentsObjectSelector',
                    labelKey: 'OrderStep.hawbDocumentCopy',
                },
                {
                    name: 'transportModeRoad',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(TransportModeRoad),
                    labelKey: 'OrderStep.transportModeRoad',
                },
                ...transportModeRoad === TransportModeRoad.FTL ? trucksAndVans as [] : [],
                {
                    name: 'isTemperatureControlled',
                    componentName: 'Checkbox',
                    labelKey: 'OrderStep.isTemperatureControlled',
                },
            ],
        },
        {
            id: 'column2',
            fields: [
                {
                    name: 'pickupLocation',
                    componentName: 'LocationObjectSelector',
                    isShippingLocation: true,
                    labelKey: 'OrderStep.pickupLocation',
                },
                ...isFirstStep
                    ? pickupLocationContactsByLocationField
                    : pickupLocationContactsField,
                {
                    name: 'requestedPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.requestedPickup',
                },
                {
                    name: 'estimatedPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.estimatedPickup',
                },
                {
                    name: 'actualPickup',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: pickupLocationId,
                    labelKey: 'OrderStep.actualPickup',
                },
                {
                    name: 'pickupComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.pickupComment',
                },
                {
                    name: 'defaultTruckingComment',
                    componentName: 'EnumSelector',
                    allowedValues: getAllowedValues(DefaultTruckingComment),
                    useDropdown: true,
                    labelKey: 'OrderStep.defaultTruckingComment',
                },
            ],
        },
        {
            id: 'column3',
            fields: [
                {
                    name: 'deliveryLocation',
                    componentName: 'LocationObjectSelector',
                    isShippingLocation: true,
                    labelKey: 'OrderStep.deliveryLocation',
                },
                {
                    name: 'deliveryLocationContacts',
                    componentName: 'MultipleContactByLocationObjectSelector',
                    existedForm: exists,
                    selectedLocation: deliveryLocationId,
                    labelKey: 'OrderStep.deliveryLocationContacts',
                },
                {
                    name: 'requestedDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.requestedDelivery',
                },
                {
                    name: 'estimatedDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.estimatedDelivery',
                },
                {
                    name: 'actualDelivery',
                    componentName: 'DateTimeZonedFromLocation',
                    locationId: deliveryLocationId,
                    labelKey: 'OrderStep.actualDelivery',
                },
                {
                    name: 'deliveryComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.deliveryComment',
                },
                {
                    name: 'truckingComment',
                    componentName: 'LongText',
                    className: 'truckingComment',
                    labelKey: 'OrderStep.truckingComment',
                },
            ],
        },
        {
            id: 'column4',
            fields: [
                {
                    name: 'flightNumber',
                    componentName: 'InputSingleline',
                    labelKey: 'OrderStep.flightNumber',
                },
                {
                    name: 'airline',
                    componentName: 'CompanyObjectSelector',
                    labelKey: 'OrderStep.airline',
                },
                {
                    name: 'originAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType,
                    labelKey: 'OrderStep.originAirport',
                },
                {
                    name: 'destinationAirport',
                    componentName: 'LocationObjectSelector',
                    filterLocationType,
                    labelKey: 'OrderStep.destinationAirport',
                },
                ...stepTypeDetailed !== StepTypeDetailed.AIR_INTERNAL
                && stepTypeDetailed !== StepTypeDetailed.AIR_ROAD_INTERNAL ? [{
                    name: 'estimatedFlightDeparture',
                    componentName: 'DateTimeZonedSelect',
                    labelKey: 'OrderStep.estimatedFlightDeparture',
                }] as NestedField<OrderStepFields>[] : [],
                {
                    name: 'actualFlightDeparture',
                    componentName: 'DateTimeZonedSelect',
                    labelKey: 'OrderStep.actualFlightDeparture',
                },
                ...stepTypeDetailed !== StepTypeDetailed.AIR_INTERNAL
                && stepTypeDetailed !== StepTypeDetailed.AIR_ROAD_INTERNAL ? [{
                    name: 'estimatedFlightArrival',
                    componentName: 'DateTimeZonedSelect',
                    labelKey: 'OrderStep.estimatedFlightArrival',
                }] as NestedField<OrderStepFields>[] : [],
                {
                    name: 'actualFlightArrival',
                    componentName: 'DateTimeZonedSelect',
                    labelKey: 'OrderStep.actualFlightArrival',
                },
                {
                    name: 'flightComment',
                    componentName: 'LongText',
                    labelKey: 'OrderStep.flightComment',
                },
            ],
        },
    ]
}

export default getInternalAirFormFields
